import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CourierCompanyService } from 'src/app/services/courier-company/courier-company.service';
import { CourierCompanyModalService } from 'src/app/services/courier-company-modal/courier-company-modal.service';
import { PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-md-courier-company',
  templateUrl: './md-courier-company.component.html',
  styleUrls: ['./md-courier-company.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateY(0%)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class MdCourierCompanyComponent extends DestructorComponent implements OnInit {

  courierCompanies: any[] = [];
  isLoading: boolean = false;
  searchTerm: string = '';
  showFiltersForm: boolean = false;
  currentPage: number = 1;

  // Pagination
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Filters
  filters: any = {
    search: '',
    date_from: '',
    date_to: ''
  };

  constructor(
    private _courierCompanyService: CourierCompanyService,
    private _courierCompanyModalService: CourierCompanyModalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCourierCompanies();
  }

  loadCourierCompanies(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    this.subscriptions.push(
      this._courierCompanyService.GetData(page.toString(), search, filters).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status && response.data) {
            this.courierCompanies = response.data.data || [];

            // Update pagination info
            if (response.data.pagination) {
              this.paginationInfo = {
                currentPage: response.data.pagination.current_page || page,
                perPage: response.data.pagination.per_page || 20,
                total: response.data.pagination.total || 0,
                lastPage: response.data.pagination.last_page || 1,
                firstPage: 1
              };
            } else {
              this.resetPagination();
            }
          } else {
            this.courierCompanies = [];
            this.resetPagination();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.error('Error loading courier companies:', error);
          this.courierCompanies = [];
          this.resetPagination();
        }
      })
    );
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  onPageChange(page: number): void {
    this.loadCourierCompanies(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadCourierCompanies(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: ''
    };
    this.searchTerm = '';
    this.loadCourierCompanies();
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  openAddCourierCompanyModal(): void {
    this._courierCompanyModalService.clearSelectedCourierCompany();
    const modalElement = document.getElementById('kt_modal_courier_company_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditCourierCompanyModal(courierCompany: any): void {
    this._courierCompanyModalService.setSelectedCourierCompany(courierCompany);
    const modalElement = document.getElementById('kt_modal_courier_company_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteCourierCompany(courierCompany: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${courierCompany.company_name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(courierCompany);
      }
    });
  }

  private performDelete(courierCompany: any): void {
    const courierCompanyId = courierCompany._id || courierCompany.id;
    this.subscriptions.push(
      this._courierCompanyService.DeleteDataById(courierCompanyId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Courier company has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCourierCompanies(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete courier company.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting courier company:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting the courier company.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onCourierCompanyAdded(): void {
    this.loadCourierCompanies(this.currentPage, this.searchTerm, this.filters);
  }

  onCourierCompanyUpdated(): void {
    this.loadCourierCompanies(this.currentPage, this.searchTerm, this.filters);
  }

}
